(function () {
  'use strict';

  const PRODUCT_KEY_ALIASES = {
    'personalised-photo-projection-bracelet-custom-picture-bracelet-for-men-women': 'personalised-classic-loop-photo-projection-bracelet',
    'personalised-photo-projection-heart-bracelet-custom-picture-gift': 'personalised-heart-shape-photo-projection-bracelet',
    'personalised-mum-photo-projection-necklace-custom-mom-heart-gift': 'personalised-classic-mom-script-heart-photo-projection-necklace',
    'personalised-pet-photo-projection-necklace-custom-paw-keepsake': 'personalised-love-paw-photo-projection-necklace',
    'personalised-photo-projection-heart-necklace-custom-picture-gift-for-her': 'personalised-classic-love-heart-photo-projection-necklace',
    'personalised-tree-of-life-photo-projection-necklace-custom-family-gift': 'personalised-tree-of-life-photo-projection-necklace'
  };
  const cache = new Map();

  const normalizeProductKey = (value) => {
    let key = String(value || '').trim().toLowerCase();
    if (!key) return '';
    try { if (/^https?:\/\//i.test(key)) key = new URL(key).pathname; } catch (_) {}
    key = key.replace(/^\/?products\//, '').split(/[?#]/)[0].replace(/\/+$/, '').replace(/-silver$/, '');
    return PRODUCT_KEY_ALIASES[key] || key;
  };

  const productKeysMatch = (a, b) => normalizeProductKey(a) === normalizeProductKey(b);

  const parseCsv = (text) => {
    const input = String(text || '').replace(/^\uFEFF/, '');
    const rows = [];
    let row = [], field = '', quoted = false;
    for (let i = 0; i < input.length; i += 1) {
      const character = input[i];
      if (quoted) {
        if (character === '"') {
          if (input[i + 1] === '"') { field += '"'; i += 1; }
          else quoted = false;
        } else field += character;
      } else if (character === '"') quoted = true;
      else if (character === ',') { row.push(field); field = ''; }
      else if (character === '\n' || character === '\r') {
        if (character === '\r' && input[i + 1] === '\n') i += 1;
        row.push(field); field = '';
        if (row.some((value) => value !== '')) rows.push(row);
        row = [];
      } else field += character;
    }
    if (field !== '' || row.length) {
      row.push(field);
      if (row.some((value) => value !== '')) rows.push(row);
    }
    if (!rows.length) return [];
    const headers = rows.shift().map((value) => String(value || '').trim());
    return rows.map((values) => {
      const record = {};
      headers.forEach((header, index) => { if (header) record[header] = values[index] ?? ''; });
      return record;
    });
  };

  const parseBoolean = (value) => /^(true|1|yes)$/i.test(String(value || '').trim());
  const parseRating = (value) => {
    const clean = String(value ?? '').trim();
    if (!clean) return null;
    const number = Number(clean);
    return Number.isFinite(number) && number >= 1 && number <= 5 ? number : null;
  };
  const parseImages = (value) => {
    const clean = String(value || '').trim();
    if (!clean) return [];
    try {
      const parsed = JSON.parse(clean);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
      if (typeof parsed === 'string') return [parsed];
    } catch (_) {}
    return clean.split(/[|\n]/).map((item) => item.trim()).filter(Boolean);
  };
  const normalizeMediaUrl = (value) => {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
    } catch (_) { return null; }
  };

  const utcTimestamp = (year, month, day) => {
    const timestamp = Date.UTC(year, month - 1, day);
    const date = new Date(timestamp);
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? timestamp : 0;
  };
  const dateValue = (value) => {
    const clean = String(value || '').trim();
    if (!clean) return 0;
    let match = clean.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (match) return utcTimestamp(Number(match[1]), Number(match[2]), Number(match[3]));
    match = clean.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2}|\d{4})$/);
    if (match) {
      const day = Number(match[1]);
      const month = Number(match[2]);
      let year = Number(match[3]);
      if (year < 100) year += 2000;
      return utcTimestamp(year, month, day);
    }
    const timestamp = Date.parse(clean);
    return Number.isFinite(timestamp) ? timestamp : 0;
  };
  const formatDate = (value) => {
    const timestamp = dateValue(value);
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
  };
  const formatReviewerName = (value) => {
    const clean = String(value || '').replace(/\s+/g, ' ').trim();
    if (!clean) return 'Customer';
    const parts = clean.split(' ').filter(Boolean);
    if (parts.length === 1) return parts[0];
    const last = parts[parts.length - 1].replace(/\./g, '');
    return last ? `${parts[0]} ${last.charAt(0).toUpperCase()}.` : parts[0];
  };
  const badgeLabel = (value) => {
    const clean = String(value || '').trim();
    if (!clean) return '';
    if (/^verified[\s_-]*buyer$/i.test(clean)) return 'Verified Buyer';
    return clean.replace(/_/g, ' ');
  };
  const starString = (rating) => {
    const rounded = Math.round(Math.min(5, Math.max(1, Number(rating) || 1)));
    return `${'★'.repeat(rounded)}${'☆'.repeat(5 - rounded)}`;
  };

  const normalizeText = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFKC')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const trigramSet = (value) => {
    const source = ` ${value} `;
    const grams = new Set();
    for (let index = 0; index <= source.length - 3; index += 1) grams.add(source.slice(index, index + 3));
    return grams;
  };

  const diceSimilarity = (left, right) => {
    if (left === right) return 1;
    if (!left || !right) return 0;
    const leftGrams = trigramSet(left);
    const rightGrams = trigramSet(right);
    let overlap = 0;
    leftGrams.forEach((gram) => { if (rightGrams.has(gram)) overlap += 1; });
    return (2 * overlap) / Math.max(1, leftGrams.size + rightGrams.size);
  };

  const normalizeRow = (row, index) => {
    const rating = parseRating(row.rating);
    const hasRatingRule = Object.prototype.hasOwnProperty.call(row, 'counts_toward_rating') && String(row.counts_toward_rating || '').trim() !== '';
    return {
      id: String(row.id || row.review_id || index).trim(),
      product_key: normalizeProductKey(row.product_key || row.product || row.product_handle || ''),
      scope: String(row.review_scope || row.scope || 'product').trim().toLowerCase(),
      type: String(row.review_type || row.type || 'review').trim().toLowerCase(),
      rating,
      title: String(row.title || '').trim(),
      body: String(row.body || '').trim(),
      name: String(row.name || row.reviewer_name || '').trim() || 'Customer',
      date: String(row.date || row.review_date || '').trim(),
      location: String(row.location || row.reviewer_location || '').trim(),
      badge: String(row.badge || (parseBoolean(row.verified) ? 'Verified Buyer' : '')).trim(),
      images: parseImages(row.image_urls || row.media_urls),
      verified: parseBoolean(row.verified) || /^verified[\s_-]*buyer$/i.test(String(row.badge || '')),
      published: parseBoolean(row.published),
      countsTowardRating: hasRatingRule ? parseBoolean(row.counts_toward_rating) : rating !== null
    };
  };

  const dedupe = (rows) => {
    const groups = new Map();
    rows.forEach((row) => {
      const key = row.product_key || 'global';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push({ row, copy: normalizeText(`${row.title} ${row.body}`), suppressed: false });
    });
    groups.forEach((reviews) => {
      reviews.forEach((current, currentIndex) => {
        if (!current.row.published || current.suppressed || !current.copy) return;
        const start = Math.max(0, currentIndex - 10);
        for (let previousIndex = start; previousIndex < currentIndex; previousIndex += 1) {
          const previous = reviews[previousIndex];
          if (!previous.row.published || previous.suppressed || !previous.copy) continue;
          const exact = previous.copy === current.copy && Math.min(previous.copy.length, current.copy.length) >= 18;
          const fuzzy = previous.copy !== current.copy && Math.min(previous.copy.length, current.copy.length) >= 40 && diceSimilarity(previous.copy, current.copy) >= 0.88;
          if (!exact && !fuzzy) continue;
          current.suppressed = true;
          break;
        }
      });
    });
    const suppressed = new Set();
    groups.forEach((reviews) => reviews.forEach((item) => { if (item.suppressed) suppressed.add(item.row); }));
    return rows.map((row) => suppressed.has(row) ? Object.assign({}, row, { published: false }) : row);
  };

  const rowsFor = (rows, productKey) => rows.filter((row) =>
    row.published &&
    row.scope === 'product' &&
    row.type === 'review' &&
    productKeysMatch(row.product_key, productKey)
  );

  const summaryFor = (rows, productKey) => {
    const reviews = rowsFor(rows, productKey);
    const rated = reviews.filter((row) => row.rating !== null && row.countsTowardRating);
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    rated.forEach((row) => {
      const key = Math.round(row.rating);
      if (key >= 1 && key <= 5) distribution[key] += 1;
    });
    const average = rated.length ? rated.reduce((sum, row) => sum + row.rating, 0) / rated.length : 0;
    const positive = rated.length ? Math.round(((distribution[5] + distribution[4]) / rated.length) * 100) : 0;
    return { reviews, rated, distribution, average, positive, count: reviews.length };
  };

  const load = (url) => {
    const key = String(url || '').trim();
    if (!key) return Promise.reject(new Error('Review CSV URL is missing'));
    if (!cache.has(key)) {
      cache.set(key, fetch(key, { method: 'GET', cache: 'no-store', headers: { Accept: 'text/csv' } })
        .then((response) => {
          if (!response.ok) throw new Error(`CSV request failed with ${response.status}`);
          return response.text();
        })
        .then((text) => dedupe(parseCsv(text).map(normalizeRow)))
        .catch((error) => {
          cache.delete(key);
          throw error;
        }));
    }
    return cache.get(key);
  };

  const engine = {
    load,
    rowsFor,
    summaryFor,
    normalizeProductKey,
    productKeysMatch,
    normalizeMediaUrl,
    dateValue,
    formatDate,
    formatReviewerName,
    badgeLabel,
    starString
  };
  window.PhilUReviews = engine;

  const config = document.querySelector('[data-philu-review-config]');
  const csvUrl = config && String(config.dataset.csvUrl || '').trim();
  if (!csvUrl) return;

  load(csvUrl).then((rows) => {
    document.querySelectorAll('[data-philu-review-summary]').forEach((element) => {
      const summary = summaryFor(rows, element.dataset.productKey);
      if (!summary.count || !summary.rated.length) {
        if (!element.textContent.trim()) element.hidden = true;
        return;
      }
      const score = element.querySelector('[data-review-summary-score]');
      const count = element.querySelector('[data-review-summary-count]');
      if (score) score.textContent = summary.average.toFixed(1);
      if (count) count.textContent = String(summary.count);
      element.hidden = false;
    });

    document.querySelectorAll('[data-philu-review-teaser]').forEach((element) => {
      const summary = summaryFor(rows, element.dataset.productKey);
      if (!summary.count || !summary.rated.length) {
        if (!element.textContent.trim()) element.hidden = true;
        return;
      }
      const score = element.querySelector('[data-review-teaser-score]');
      const count = element.querySelector('[data-review-teaser-count]');
      const quotes = element.querySelector('[data-review-teaser-quotes]');
      if (score) score.textContent = summary.average.toFixed(1);
      if (count) count.textContent = `${summary.count} reviews`;
      if (quotes) {
        quotes.textContent = '';
        summary.reviews
          .filter((review) => review.rating >= 4 && review.body.length >= 35)
          .sort((left, right) => dateValue(right.date) - dateValue(left.date))
          .slice(0, 2)
          .forEach((review) => {
            const quote = document.createElement('blockquote');
            quote.className = 'product-review-teaser__quote';
            const body = document.createElement('p');
            const cite = document.createElement('cite');
            body.textContent = `“${review.body}”`;
            cite.textContent = `— ${formatReviewerName(review.name)}`;
            quote.append(body, cite);
            quotes.appendChild(quote);
          });
      }
      element.hidden = false;
    });
  }).catch((error) => {
    console.error('[PhilU reviews]', error);
  });
})();